import React, { useState, useEffect, ChangeEvent } from "react";
import { Container, Alert, ConnectedAlert, ProgressBar, RadioButtonWithLabel } from "../../../../components";
import { Text, Heading, Button, TextField } from "@telia/teddy";
import moment from "moment";
import { connect, useDispatch } from "react-redux";
import PAGES from "../../../../../../constants/pages";
import { RootState } from "../../../../../../models";
import { BusinessDetailsDetails } from "../../../../../../models/state/business";
import { RejectReason, RejectReasonID } from "../../../../../../models/transfer";
import {
    isOrderNotFound as isOrderNotFoundSelector,
    getOrderItemGuid,
    getErrorMessage,
} from "../../../../app/state/selectors";
import { selectors as AUTH_SELECTORS } from "../../../../authentication";
import {
    getBankIdErrorMessage,
    isBankIdVerified as isBankIdVerifiedSelector,
} from "../../../../authentication/state/selectors";
import BankIdSection from "../../../../checkout/components/molecules/BankIdSectionTeliaBroker/BankIdSectionTeliaBroker";
import OrgOrNameInput from "../../../../checkout/components/molecules/OrgOrNameInput/OrgOrNameInput";
import { checkOrgName, checkOrgNumber } from "../../../../checkout/state/actions/businessActions";
import {
    updateFormerLegalB2B,
    rejectTransferOwnershipOrderToB2B,
    submitTransferOwnershipOrderToB2B,
    bankIdResetWhenConsentToB2B,
} from "../../../../checkout/state/actions/transferOwnershipActions";
import {
    getTransferBreakageFee,
    getSelectedPhoneNumber,
    getTransferNewLegalOwnerField,
    getTransferNewIndividualLegalOwnerFullName,
    getTransferExistingIndividualLegalOwnerFullName,
    getExistingVerifiedCompanyAdmin,
    getHasSubmittedTransferOrder,
    getHasRejectedTransferOrder,
    getTransferDate,
    getTransferExistingOrganizationName,
    getTransferExistingOrganizationNumber,
} from "../../../../checkout/state/selectors/accountSelectors";
import {
    getBrregNames,
    getOrganizationName,
    getOrganizationNumber,
} from "../../../../checkout/state/selectors/businessDetailsSelectors";
import { selectors as LOADING_SELECTORS } from "../../../../loading";
import { getFormattedPhoneNumber } from "../../../../rapidform/formValidation";
import InformationTable from "../../../components/molecules/InformationTable/InformationTable";
import TEXT from "../../../text-consts";

interface StateProps {
    bankIdCustomer?: string;
    bankIdUrl?: string;
    brRegOrganizationName: string;
    brRegOrganizationNumber: string;
    breakageFee: number;
    brregNameSearch: {
        name: string;
        orgNumber: string;
    }[];
    existingLegalOwner: string;
    hasRejectedTransferOrder: boolean;
    hasSubmittedTransferOrder: boolean;
    isBankIdVerified: boolean;
    isFetchingData: boolean;
    isOrderNotFound: boolean;
    logicalErrorMessage?: string;
    newLegalOwner: string;
    orderItemGuid: string;
    organizationName: string;
    selectedPhoneNumber: string;
    shouldShowBankId: boolean;
    transferDate?: string;
    verifiedCompanyAdmin: string;
}

type ConfirmPageToB2BProps = StateProps;

interface AcceptContentProps {
    bankIdType?: string | null;
    bankIdUrl?: string;
    brRegOrganizationName: string;
    brRegOrganizationNumber: string;
    brregNameSearch: {
        name: string;
        orgNumber: string;
    }[];
    existingLegalOwner?: string;
    isB2BTransfer: boolean;
    isBankIdVerified: boolean;
    newLegalOwner: string;
    orderItemGuid: string;
    organizationName: string;
    selectedPhoneNumber: string;
    setRejectReasons: React.Dispatch<React.SetStateAction<RejectReasonID | null>>;
    setSubSelectedOption: React.Dispatch<React.SetStateAction<"accept-b2b" | "accept-b2c" | null>>;
    subSelectedOption: string | null;
    verifiedCompanyAdmin?: string;
}

interface RejectContentProps {
    handleReasonChange: (e: ChangeEvent<HTMLInputElement>) => void;
    handleRejectReasons: (id: RejectReasonID) => void;
    reasons: RejectReason[];
    rejectReason: string;
    rejectReasons: RejectReasonID | null;
    setSubSelectedOption: React.Dispatch<React.SetStateAction<"accept-b2b" | "accept-b2c" | null>>;
}

const AcceptContent: React.FC<AcceptContentProps> = ({
    isB2BTransfer,
    organizationName,
    newLegalOwner,
    selectedPhoneNumber,
    isBankIdVerified,
    existingLegalOwner,
    verifiedCompanyAdmin,
    subSelectedOption,
    brregNameSearch,
    brRegOrganizationNumber,
    brRegOrganizationName,
    orderItemGuid,
    setSubSelectedOption,
    setRejectReasons,
}) => {
    const dispatch = useDispatch();

    return (
        <div className="tlo-landing-page__consent-expanded">
            {!isBankIdVerified && (
                <section>
                    <div
                        className="paragraph"
                        dangerouslySetInnerHTML={{
                            __html: TEXT.LANDING_PAGE_B2B_IDENTIFICATION.IDENTIFICATION_TEXT(
                                isB2BTransfer ? organizationName : newLegalOwner,
                                getFormattedPhoneNumber(selectedPhoneNumber),
                            ),
                        }}
                        data-di-mask
                    />
                    <div
                        className="paragraph paragraph--fineprint"
                        dangerouslySetInnerHTML={{
                            __html: TEXT.LANDING_PAGE_B2B_IDENTIFICATION.IDENTIFICATION_TEXT_2,
                        }}
                    />
                </section>
            )}
            {isBankIdVerified && (existingLegalOwner || verifiedCompanyAdmin) && (
                <>
                    <Text mb="100" variant="additional-100">
                        {TEXT.LANDING_PAGE_B2B_APPROVAL.BANK_ID_VERIFIED}
                    </Text>
                    <div className="tlo-landing-page__consent-expanded bankid-information" data-di-mask>
                        <Text mb="100" variant="additional-100" className="telia-sans-medium">
                            {existingLegalOwner || verifiedCompanyAdmin}
                        </Text>
                    </div>
                </>
            )}
            <div className="tlo-landing-page__consent-option-b2b">
                {!isBankIdVerified && (
                    <RadioButtonWithLabel
                        value="accept-b2b"
                        onChange={(event) => {
                            setSubSelectedOption(event.target.value as "accept-b2b" | "accept-b2c");
                            setRejectReasons(null);
                        }}
                        name="tlo-accept-b2b"
                        checked={subSelectedOption === "accept-b2b"}
                        label="Jeg godkjenner på vegne av en bedrift"
                    />
                )}
                {subSelectedOption === "accept-b2b" && (
                    <div className="tlo-landing-page__subaccept-company">
                        {(!isBankIdVerified || (isBankIdVerified && !brRegOrganizationName)) && (
                            <OrgOrNameInput
                                checkOrgName={(name) => dispatch(checkOrgName(name))}
                                checkOrgNumber={async (orgNumber) => {
                                    const org: Partial<BusinessDetailsDetails> = await dispatch(
                                        checkOrgNumber(orgNumber),
                                    );

                                    if (org) {
                                        const orgName = org.businessName || "";
                                        const organizationNumber = org.orgNumber || "";
                                        if (orgName !== "" && organizationNumber !== "") {
                                            await dispatch(
                                                updateFormerLegalB2B(orderItemGuid, orgName, organizationNumber),
                                            );
                                        }
                                    }
                                }}
                                businesses={brregNameSearch}
                                data-di-mask
                            />
                        )}
                        {brRegOrganizationName && (
                            <div className="tlo-landing-page__selected-company">
                                Valgt organisasjon: <span className="telia-sans-medium">{brRegOrganizationName}</span>{" "}
                                (org.nr.: {brRegOrganizationNumber})
                            </div>
                        )}
                    </div>
                )}
                {!isBankIdVerified && (
                    <RadioButtonWithLabel
                        value="accept-b2c"
                        onChange={(event) => {
                            setSubSelectedOption(event.target.value as "accept-b2b" | "accept-b2c");
                            setRejectReasons(null);
                        }}
                        name="tlo-accept-b2b"
                        checked={subSelectedOption === "accept-b2c"}
                        label="Jeg godkjenner som privatperson"
                    />
                )}
                {isBankIdVerified && (subSelectedOption === "accept-b2b" || subSelectedOption === "accept-b2c") && (
                    <div className="tlo-landing-page__start-over-container">
                        <Text mb="100">
                            {TEXT.LANDING_PAGE_B2B_APPROVAL.START_OVER(subSelectedOption === "accept-b2b")}
                        </Text>
                        <Button
                            size="sm"
                            variant="primary"
                            onClick={() => {
                                setSubSelectedOption(null);
                                dispatch(bankIdResetWhenConsentToB2B());
                            }}
                        >
                            Start på nytt
                        </Button>
                    </div>
                )}
            </div>
            {!isBankIdVerified &&
                (subSelectedOption === "accept-b2c" ||
                    (subSelectedOption === "accept-b2b" && brRegOrganizationName)) && (
                    <Container className="tlo-landing-page__bankid-container">
                        <BankIdSection
                            referer="consentB2B"
                            typeId="consent_b2b"
                            skipDisplayHeading={true}
                            skipDisplayError={true}
                            skipDisplayDescription={true}
                        />
                    </Container>
                )}
        </div>
    );
};

const RejectContent: React.FC<RejectContentProps> = ({
    reasons,
    rejectReason,
    rejectReasons,
    handleRejectReasons,
    handleReasonChange,
    setSubSelectedOption,
}) => (
    <div className="tlo-landing-page__consent-expanded">
        <Text mb="100" variant="additional-100">
            {TEXT.LANDING_PAGE_B2B_APPROVAL.REJECT_DESCRIPTION}
        </Text>
        <div className="tlo-landing-page__consent-reject-container">
            {reasons.map((reason) => (
                <div key={reason.id} className="tlo-landing-page__consent-reject-option">
                    <RadioButtonWithLabel
                        checked={rejectReasons === reason.id}
                        label={reason.text}
                        onChange={() => {
                            handleRejectReasons(reason.id);
                            setSubSelectedOption(null);
                        }}
                    />
                    {rejectReasons === "OTHER" && rejectReasons === reason.id && (
                        <TextField className="reject-reason">
                            <TextField.Input
                                placeholder="Beskriv kort årsaken"
                                value={rejectReason}
                                onChange={handleReasonChange}
                            />
                        </TextField>
                    )}
                </div>
            ))}
        </div>
    </div>
);

const ConfirmPageToB2B: React.FC<ConfirmPageToB2BProps> = ({
    // Redux (data)
    isFetchingData,
    isOrderNotFound,
    orderItemGuid,
    bankIdUrl,
    organizationName,
    newLegalOwner,
    breakageFee,
    brregNameSearch,
    brRegOrganizationName,
    brRegOrganizationNumber,
    existingLegalOwner,
    verifiedCompanyAdmin,
    selectedPhoneNumber,
    transferDate,
    hasSubmittedTransferOrder,
    hasRejectedTransferOrder,
    isBankIdVerified,
    logicalErrorMessage,
}) => {
    const dispatch = useDispatch();
    const [rejectReason, setRejectReason] = useState("");
    const [showRejectReasonInput, setRejectReasonInput] = useState(false);
    const [hasClickedSubmit, setHasClickedSubmit] = useState(false);
    const [hasClickedRejected, setHasClickedRejected] = useState(false);
    const [selectedOption, setSelectedOption] = useState<"accept" | "reject" | null>(
        isBankIdVerified ? "accept" : null,
    );
    const [subSelectedOption, setSubSelectedOption] = useState<"accept-b2c" | "accept-b2b" | null>(
        isBankIdVerified && brRegOrganizationName ? "accept-b2b" : isBankIdVerified ? "accept-b2c" : null,
    );
    const [rejectReasons, setRejectReasons] = useState<RejectReasonID | null>(null);
    const isB2BTransfer = !!organizationName;

    const reasons: RejectReason[] = [
        { id: "NOT_OWNER", text: "Jeg er ikke eier av dette nummeret" },
        { id: "OTHER", text: "Annet", reason: rejectReason },
    ];

    if (breakageFee) {
        reasons.splice(2, 0, { id: "NOT_ACCEPTABLE_FEE", text: "Jeg aksepterer ikke bruddgebyret" });
    }

    useEffect(() => {
        if (hasSubmittedTransferOrder || hasRejectedTransferOrder) {
            window.location.assign(`${PAGES.TRANSFER_OWNERSHIP.LANDING_PAGE_TO_B2B.SUMMARY}${orderItemGuid}`);
        }
    }, [hasSubmittedTransferOrder, hasRejectedTransferOrder, orderItemGuid]);

    const handleReasonChange = (e: ChangeEvent<HTMLInputElement>) => {
        setRejectReason((e.target as HTMLInputElement).value);
    };

    const handleSubmitTransferOwnershipOrder = async () => {
        setHasClickedSubmit(true);
        await dispatch(
            submitTransferOwnershipOrderToB2B(orderItemGuid, brRegOrganizationName, brRegOrganizationNumber),
        );
    };

    const handleRejectReasons = (id: RejectReasonID) => {
        if (id === "OTHER") {
            setRejectReasonInput(!showRejectReasonInput);
        }
        setRejectReasons(id);
    };

    const handleRejectTransferOwnershipOrder = async () => {
        setHasClickedRejected(true);
        const reason = reasons.find((r) => r.id === rejectReasons);

        if (rejectReasons && reason) {
            let reasonText = "";
            let reasonId = "OTHER";

            if (rejectReasons === "OTHER" && rejectReason) {
                reasonText = rejectReason;
            } else {
                reasonText = reason.text;
                reasonId = rejectReasons;
            }

            await dispatch(rejectTransferOwnershipOrderToB2B(reasonText, reasonId, orderItemGuid));
        }
    };

    const backToFrontPage = () => {
        window.location.assign("https://www.telia.no/");
    };

    if (isOrderNotFound) {
        return (
            <div className="tlo-landing-page">
                <div className="tlo-landing-page__heading-container">
                    <Heading mb="100" as="h1" variant="title-600">
                        {TEXT.LANDING_PAGE_TO_B2B.HEADING}
                    </Heading>
                </div>
                <Container size="medium">
                    <section className="tlo-landing-page__order-not-found">
                        <Heading mb="100" as="h2" variant="title-200">
                            {TEXT.LANDING_PAGE_TO_B2B.ORDER_NOT_FOUND.HEADING}
                        </Heading>
                        <Text mb="100">{TEXT.LANDING_PAGE_TO_B2B.ORDER_NOT_FOUND.TEXT}</Text>
                        <div
                            className="paragraph"
                            dangerouslySetInnerHTML={{ __html: TEXT.LANDING_PAGE_TO_B2B.ORDER_NOT_FOUND.HELP_TEXT }}
                        />
                    </section>
                </Container>
            </div>
        );
    }

    const data = [
        { label: "Overføres til:", value: isB2BTransfer ? organizationName : newLegalOwner },
        { label: "Telefonnummer:", value: getFormattedPhoneNumber(selectedPhoneNumber) },
        {
            label: "Ønsket overføringsdato:",
            value: transferDate ? moment(transferDate).format("DD.MM.YYYY") : "Ingen dato angitt",
        },
    ];

    if (breakageFee) {
        data.push({ label: "Bruddgebyr:", value: `${breakageFee},-` });
    }

    if (hasSubmittedTransferOrder) {
        return (
            <div className="tlo-landing-page">
                <div className="tlo-landing-page__heading-container">
                    <Heading mb="100" as="h1" variant="title-600">
                        {TEXT.LANDING_PAGE_TO_B2B.HEADING}
                    </Heading>
                </div>
                <Container size="medium">
                    <section>
                        <Heading mb="100" as="h2" variant="title-200">
                            {TEXT.LANDING_PAGE_B2B_APPROVED.APPROVAL_HEADING}
                        </Heading>
                        <Text mb="100">{TEXT.LANDING_PAGE_B2B_APPROVED.RECEIVED_APPROVAL}</Text>
                        <InformationTable data={data} withBorder={true} withHr={false} data-di-mask />
                    </section>
                    <section className="tlo-landing-page__back-to-frontpage">
                        <Button variant="primary" type="submit" onClick={backToFrontPage} disabled={false}>
                            {TEXT.LANDING_PAGE_B2B_REJECTION.BUTTON_TEXT}
                        </Button>
                    </section>
                </Container>
            </div>
        );
    }

    return (
        <div className="tlo-landing-page">
            <div className="tlo-landing-page__heading-container">
                <Heading mb="100" as="h1" variant="title-600">
                    {TEXT.LANDING_PAGE_TO_B2B.HEADING}
                </Heading>
            </div>
            <ProgressBar isFetchingData={isFetchingData} backgroundColor="white" />
            <ConnectedAlert />
            {logicalErrorMessage && (
                <Container size="small">
                    <Alert kind="warning">{logicalErrorMessage}</Alert>
                </Container>
            )}
            {(organizationName || newLegalOwner) && (
                <Container size="medium">
                    <div
                        className="paragraph"
                        dangerouslySetInnerHTML={{
                            __html: TEXT.LANDING_PAGE_TO_B2B.REQUEST_RECEIVED(
                                isB2BTransfer ? organizationName : newLegalOwner,
                                getFormattedPhoneNumber(selectedPhoneNumber),
                                transferDate ? moment(transferDate).format("DD.MM.YYYY") : "ved ingen dato angitt",
                            ),
                        }}
                        data-di-mask
                    />
                    <InformationTable data={data} withBorder={true} withHr={false} data-di-mask />
                    <Text mb="100" data-di-mask>
                        {TEXT.LANDING_PAGE_B2B_APPROVAL.RECEIVED_APPROVAL_TEXT(breakageFee)}
                    </Text>
                    <Container size="medium" whiteBg className="tlo-landing-page__section">
                        <div className="tlo-landing-page__consent-option">
                            <RadioButtonWithLabel
                                value="accept"
                                onChange={(event) => {
                                    setSelectedOption(event.target.value as "accept" | "reject");
                                    setSubSelectedOption(null);
                                    setRejectReasons(null);
                                }}
                                name="consentB2B"
                                checked={selectedOption === "accept"}
                                hasRichContent
                            >
                                <Heading
                                    mb="100"
                                    as="h3"
                                    variant="title-100"
                                    className="tlo-landing-page__consent-radio-heading"
                                >
                                    {"Jeg godkjenner overføringen"}
                                </Heading>
                            </RadioButtonWithLabel>
                            {selectedOption === "accept" && (
                                <AcceptContent
                                    isB2BTransfer={isB2BTransfer}
                                    organizationName={organizationName}
                                    newLegalOwner={newLegalOwner}
                                    selectedPhoneNumber={selectedPhoneNumber}
                                    isBankIdVerified={isBankIdVerified}
                                    existingLegalOwner={existingLegalOwner}
                                    verifiedCompanyAdmin={verifiedCompanyAdmin}
                                    subSelectedOption={subSelectedOption}
                                    brregNameSearch={brregNameSearch}
                                    brRegOrganizationNumber={brRegOrganizationNumber}
                                    brRegOrganizationName={brRegOrganizationName}
                                    bankIdUrl={bankIdUrl}
                                    setSubSelectedOption={setSubSelectedOption}
                                    setRejectReasons={setRejectReasons}
                                    orderItemGuid={orderItemGuid}
                                />
                            )}
                        </div>
                        <div className="tlo-landing-page__consent-option">
                            <RadioButtonWithLabel
                                value="reject"
                                onChange={(event) => {
                                    setSelectedOption(event.target.value as "accept" | "reject");
                                    setSubSelectedOption(null);
                                    setRejectReasons(null);
                                }}
                                name="consentB2B"
                                checked={selectedOption === "reject"}
                                hasRichContent
                            >
                                <Heading
                                    mb="100"
                                    as="h3"
                                    variant="title-100"
                                    className="tlo-landing-page__consent-radio-heading"
                                >
                                    {"Jeg avslår overføringen"}
                                </Heading>
                            </RadioButtonWithLabel>
                            {selectedOption === "reject" && (
                                <RejectContent
                                    handleReasonChange={handleReasonChange}
                                    handleRejectReasons={handleRejectReasons}
                                    reasons={reasons}
                                    rejectReason={rejectReason}
                                    rejectReasons={rejectReasons}
                                    setSubSelectedOption={setSubSelectedOption}
                                />
                            )}
                        </div>
                    </Container>
                    <Container size="medium" className="tlo-landing-page__right-align">
                        {selectedOption === "accept" && isBankIdVerified && (
                            <Button
                                variant="primary"
                                disabled={!(existingLegalOwner || verifiedCompanyAdmin) || hasClickedSubmit}
                                onClick={handleSubmitTransferOwnershipOrder}
                            >
                                {TEXT.LANDING_PAGE_B2B_APPROVAL.BUTTON.NEXT_STEP}
                            </Button>
                        )}
                        {selectedOption === "reject" && rejectReasons && (
                            <Button
                                variant="primary"
                                type="submit"
                                disabled={hasClickedRejected}
                                onClick={handleRejectTransferOwnershipOrder}
                            >
                                {TEXT.LANDING_PAGE_B2B_APPROVAL.BUTTON.NEXT_STEP}
                            </Button>
                        )}
                    </Container>
                </Container>
            )}
        </div>
    );
};

const mapStateToProps: (state: RootState) => StateProps = (state) => ({
    brregNameSearch: getBrregNames(state),
    brRegOrganizationName: getOrganizationName(state) || getTransferExistingOrganizationName(state) || "",
    brRegOrganizationNumber: getOrganizationNumber(state) || getTransferExistingOrganizationNumber(state) || "",
    isFetchingData: LOADING_SELECTORS.isLoading(state),
    isOrderNotFound: isOrderNotFoundSelector(state),
    orderItemGuid: getOrderItemGuid(state),
    shouldShowBankId: AUTH_SELECTORS.shouldShowBankId(state),
    bankIdUrl: AUTH_SELECTORS.getBankIdUrl(state),
    breakageFee: getTransferBreakageFee(state),
    selectedPhoneNumber: getSelectedPhoneNumber(state),
    organizationName: getTransferNewLegalOwnerField(state, "organization", "name"),
    newLegalOwner: getTransferNewIndividualLegalOwnerFullName(state),
    existingLegalOwner: getTransferExistingIndividualLegalOwnerFullName(state) || "",
    verifiedCompanyAdmin: getExistingVerifiedCompanyAdmin(state),
    hasSubmittedTransferOrder: getHasSubmittedTransferOrder(state),
    hasRejectedTransferOrder: getHasRejectedTransferOrder(state),
    transferDate: getTransferDate(state),
    isBankIdVerified: isBankIdVerifiedSelector(state),
    logicalErrorMessage: getErrorMessage(state) || getBankIdErrorMessage(state),
});

export default connect(mapStateToProps)(ConfirmPageToB2B);
